/* eslint-disable react/prop-types */
import {
  ArrowIcon,
  ButtonIcon,
  Callout,
  Caption,
  CropIcon,
  css,
  Notes,
  styled,
  Tooltip,
} from 'canoe';
import React, { useRef, useState } from 'react';

import { cropToProps } from 'craft/components/SelectedToolbar/ImageCrop/utils/helpers';
import {
  CropReducerActionTypes,
  reducer,
} from 'craft/components/SelectedToolbar/ImageCrop/utils/reducer';
import { SizeType, useSize } from 'craft/components/Size';
import { useMultiNodes, useSelection } from 'craft/hooks';
import { useViewportDom } from 'craft/state/viewport/selectors';

import { ButtonFooter, CropRatioDropdown } from './components';
import CroppingTool from './components/CroppingTool';
import { UNSET_AXIS_VALUES, UNSET_MAX_VALUES, UNSET_RATIO, UNSET_SIZE_VALUES } from './constants';

const PopoverContainer = styled.div`
  ${({ theme }) => css`
    background-color: ${theme.colors.OnyxGrayD10};
    padding: ${theme.spaces.s};
    border-radius: ${theme.borderRadius.small};
  `}
`;

const RightArrowIcon = styled(ArrowIcon)`
  display: inline-flex;
  vertical-align: bottom;

  svg {
    transform: rotate(-90deg);
  }
`;

const PopoverTitle = styled(Notes)`
  ${({ theme }) => css`
    color: ${theme.colors.White};
    margin: 0;

    &:not(:first-of-type) {
      margin-top: ${theme.spaces.s};
    }
  `}
`;

const CropControlsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  ${({ theme }) => css`
    margin-top: ${theme.spaces.s};
  `}
`;

const RevertSettingsWrapper = styled.div`
  cursor: pointer;
`;

const RevertSettings = styled(Caption)`
  margin: 0;

  ${({ theme }) => css`
    color: ${theme.colors.White};
  `}
`;

const CenterCrop = styled.div`
  min-width: 300px;
  display: flex;
  justify-content: center;

  ${({ theme }) => css`
    margin-top: ${theme.spaces.s};
  `}
`;

export const ImageCrop = () => {
  const ref = useRef<HTMLButtonElement>(null);
  const { viewportWrapperEl } = useViewportDom();

  const [isToolbarVisible, setToolbarVisible] = useState(false);

  const { selection } = useSelection();

  const [
    {
      isCropRatioActive,
      cropRatio,
      naturalSizeValues,
      originalSizeValues,
      sizeValues,
      maxValues,
      axisValues,
      isResizing,
      resizeStartValues,
    },
    dispatch,
  ] = React.useReducer(reducer, {
    isCropRatioActive: false,
    cropRatio: UNSET_RATIO,
    naturalSizeValues: UNSET_SIZE_VALUES,
    originalSizeValues: UNSET_SIZE_VALUES,
    sizeValues: UNSET_SIZE_VALUES,
    maxValues: UNSET_MAX_VALUES,
    axisValues: UNSET_AXIS_VALUES,
    isResizing: false,
    resizeStartValues: { ...UNSET_SIZE_VALUES, ...UNSET_AXIS_VALUES },
  });

  const {
    props,
    collections,
    actions: { setProps },
  }: any = useMultiNodes(selection.all, (node) => ({
    collections: node?.data.custom.collections,
  }));

  const { size, setSize } = useSize(selection.all);

  const allowedFormats = /.jpg|.jpeg|.png/;

  if (
    !props.src.value ||
    selection.isEmpty ||
    selection.all.length > 1 ||
    selection.hasRootNode ||
    collections?.length > 0
  ) {
    return null;
  }

  const disabled = !allowedFormats.test(props.src.value);

  const RevertToOriginal = () => {
    dispatch({
      type: CropReducerActionTypes.Revert,
    });
  };
  const onClose = () => setToolbarVisible(false);

  const onCancel = () => {
    dispatch({
      type: CropReducerActionTypes.RatioSelect,
      payload: {
        isCropRatioActive: false,
        cropRatio: UNSET_RATIO,
      },
    });
    onClose();
  };

  const onSave = () => {
    setProps(cropToProps({ sizeValues, axisValues, originalSizeValues, naturalSizeValues }));
    // reset height to auto after crop to minimise distortion
    setSize({ ...size, height: { type: SizeType.Auto } });
    dispatch({
      type: CropReducerActionTypes.RatioSelect,
      payload: {
        isCropRatioActive: false,
        cropRatio: UNSET_RATIO,
      },
    });
    onClose();
  };

  return (
    <>
      {isToolbarVisible && (
        <Callout
          usePortal
          referenceElement={ref.current}
          onClose={() => setToolbarVisible(false)}
          closeKey="Escape"
          placement="top"
          modifiers={[
            {
              name: 'flip',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
                fallbackPlacements: ['top', 'bottom', 'left', 'right'],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
          ]}
          withBackdrop
        >
          {(calloutProps) => (
            <div
              data-craft-ignore-events
              ref={calloutProps.ref}
              style={calloutProps.style}
              {...calloutProps.attributes.popper}
            >
              <PopoverContainer>
                <PopoverTitle>
                  Image
                  <RightArrowIcon color="White" />
                  Crop
                </PopoverTitle>
                <CropControlsWrapper>
                  <CropRatioDropdown update={dispatch} currentValue={cropRatio} />
                  <RevertSettingsWrapper onClick={RevertToOriginal}>
                    <RevertSettings>Revert to Original</RevertSettings>
                  </RevertSettingsWrapper>
                </CropControlsWrapper>
                <CenterCrop>
                  <CroppingTool
                    update={dispatch}
                    imgData={{
                      src: props.src.value,
                      cropTop: props.cropTop?.value,
                      cropBottom: props.cropBottom?.value,
                      cropLeft: props.cropLeft?.value,
                      cropRight: props.cropRight?.value,
                    }}
                    isCropRatioActive={isCropRatioActive}
                    cropRatio={cropRatio}
                    originalSizeValues={originalSizeValues}
                    sizeValues={sizeValues}
                    maxValues={maxValues}
                    axisValues={axisValues}
                    isResizing={isResizing}
                    resizeStartValues={resizeStartValues}
                  />
                </CenterCrop>
                <ButtonFooter onCancel={() => onCancel()} onSave={() => onSave()} />
              </PopoverContainer>
            </div>
          )}
        </Callout>
      )}
      <Tooltip
        offset="xxs"
        placement="top"
        content={disabled ? 'Not supported for this type of image' : 'Image Crop'}
      >
        <ButtonIcon
          ref={ref}
          size="small"
          icon={CropIcon}
          radius="small"
          color="white"
          disabled={disabled}
          onClick={() => window.requestAnimationFrame(() => setToolbarVisible(!isToolbarVisible))}
        />
      </Tooltip>
    </>
  );
};
